import { VEHICLE_UNLOCKS } from '@/config/racers';
import {
  cosmeticById,
  isKitNumber,
  isTifoStyle,
  KIT_NUMBER_DEFAULT,
  TIFO_STYLE_DEFAULT,
  type CosmeticCategory,
} from '@/config/cosmetics';
import { pointsForPlace } from '@/core/Progression';
import type { SaveData, VehicleLoadout } from '@/core/SaveData';

/**
 * skillPoints.ts — the SKILL POINTS economy (M78).
 *
 * Skill Points are the player's lifetime meta-currency: every finished race
 * pays out, podium finishes pay a bonus, and a top-3 World Tour championship
 * pays a finale bonus. They unlock the garage (config VEHICLE_UNLOCKS) and
 * rank the player on the leaderboard (core/leaderboard.ts).
 *
 * TWO currencies, deliberately separate: CHAMPIONSHIP points (Progression
 * POINTS_BY_PLACE) rank racers inside one tour and never persist; SKILL points
 * (this module) are the player's own career total. A race's base Skill Points
 * equal its championship points, so the old M62 economy is a strict subset.
 *
 * SUPABASE CONTRACT — IMPLEMENTED (M100 RACER ID): every award flows through
 * ONE choke point as a structured {@link SkillAward}; the routing layer
 * (account/accountLogic.ts bankAward/bankOfflineFinale) decides where it
 * lands — the account outbox (linked device), this local wallet (kill switch
 * / Big Kid), or the guest pot (no account yet). Server side, each award is
 * a `point_events` row idempotent by a client-generated event id, and the
 * SERVER recomputes points from (kind, place) — it never trusts client
 * numbers. The wallet is server-derived (SUM of events; triggers maintain
 * profiles.total_points) with `save.lifetimePoints` as the offline cache
 * (reconcileWallet: local = server + pending outbox). A device's pre-account
 * career imports ONCE, only into a FRESH wallet (the old MAX(local, server)
 * idea double-counts across devices — superseded). Unlocked vehicles stay
 * DERIVED from the total. Nothing outside this module does points arithmetic;
 * keep these tables in sync with the `racer_id_v1` migration's award_points.
 */

/** Podium bonus per RACE finish place (index 1..3). The owner's "reward for
 *  finishing 1-3" — stacked on top of the base points. One tuning table. */
export const SKILL_RACE_PODIUM_BONUS = [0, 5, 3, 2] as const;

/** Championship bonus for finishing a TOUR top-3 overall (index 1..3).
 *  Awarded once, when the tour's final race is recorded. */
export const SKILL_TOUR_PODIUM_BONUS = [0, 25, 15, 10] as const;

/** One Skill-Point award — the ledger-event shape (see the header contract). */
export interface SkillAward {
  reason: 'race' | 'tour-finale';
  /** Base points for the race finish (= championship points for the place). */
  base: number;
  /** Podium bonus for a 1st–3rd RACE finish. */
  podiumBonus: number;
  /** Championship bonus for a 1st–3rd TOUR finish (tour-finale awards only). */
  tourBonus: number;
  /** The grand total actually added to the wallet. */
  total: number;
}

/** The Skill-Point payout for one finished race. Place 0 / DNF earns nothing. */
export function raceSkillAward(place: number): SkillAward {
  const base = pointsForPlace(place);
  const podiumBonus = base > 0 ? (SKILL_RACE_PODIUM_BONUS[place] ?? 0) : 0;
  return { reason: 'race', base, podiumBonus, tourBonus: 0, total: base + podiumBonus };
}

/** The championship bonus for a finished tour (place = final overall
 *  championship position). Off the podium → a zero award (never applied).
 *  ONLINE tours pay this EVERY time (owner rule 2026-07-15 — real
 *  competition earns repeatedly); offline goes through
 *  {@link applyOfflineTourFinale} instead. */
export function tourFinaleAward(championshipPlace: number): SkillAward {
  const tourBonus = SKILL_TOUR_PODIUM_BONUS[championshipPlace] ?? 0;
  return { reason: 'tour-finale', base: 0, podiumBonus: 0, tourBonus, total: tourBonus };
}

/**
 * OFFLINE tour finale bonus. Owner rule (2026-10-02, replacing the M79
 * "offline pays only once" rule): every top-3 championship pays its FULL
 * bonus, every time — exactly like an online tour — because some players
 * prefer offline mode. `offlineTourBonusPaid` survives only as the highest
 * tier ever paid (informational; nothing gates on it any more).
 */
export function applyOfflineTourFinale(
  save: SaveData,
  championshipPlace: number,
): { save: SaveData; award: SkillAward; newlyUnlocked: string[] } {
  const tier = SKILL_TOUR_PODIUM_BONUS[championshipPlace] ?? 0;
  const award: SkillAward = {
    reason: 'tour-finale',
    base: 0,
    podiumBonus: 0,
    tourBonus: tier,
    total: tier,
  };
  if (tier <= 0) return { save, award, newlyUnlocked: [] };
  const applied = applySkillAward(save, award);
  applied.save.offlineTourBonusPaid = Math.max(save.offlineTourBonusPaid, tier);
  return { save: applied.save, award, newlyUnlocked: applied.newlyUnlocked };
}

/**
 * Bank an award into the save's wallet and report which vehicles the crossing
 * unlocks. PURE (returns a new save; `now` injectable for tests); a zero
 * award is a no-op. Successor to M62's applyRacePoints — same crossing
 * semantics, award-shaped input. M82: the award also lands on the WEEKLY
 * counter (This-Week leaderboard), which resets on the ET-Monday boundary.
 * M114: every crossing is ALSO banked into the save's granted-forever
 * `unlockedVehicles` set, so a future threshold retune can never re-lock a
 * car this award unlocked; a car already in the set (a legacy grant) is
 * never re-reported as newly unlocked when its raised threshold is crossed.
 */
export function applySkillAward(
  save: SaveData,
  award: SkillAward,
  now: Date = new Date(),
): { save: SaveData; newlyUnlocked: string[] } {
  if (award.total <= 0) return { save, newlyUnlocked: [] };
  const before = save.lifetimePoints;
  const after = before + award.total;
  const newlyUnlocked = VEHICLE_UNLOCKS.filter(
    (u) => u.points > before && u.points <= after && !save.unlockedVehicles.includes(u.id),
  ).map((u) => u.id);
  const next = structuredClone(save);
  next.lifetimePoints = after;
  // newlyUnlocked already excludes anything in the set — plain append stays deduped.
  next.unlockedVehicles = [...save.unlockedVehicles, ...newlyUnlocked];
  const week = etWeekKey(now);
  next.weekly = {
    key: week,
    points: (save.weekly.key === week ? save.weekly.points : 0) + award.total,
  };
  return { save: next, newlyUnlocked };
}

// ---------------------------------------------------------------------------
// THIS WEEK (M82) — the weekly leaderboard window. Owner rule: resets every
// Monday morning at 12 AM ET. The week is identified by its ET-Monday date
// stamp; comparing stamps is the whole reset mechanic (no timers to run).
// ---------------------------------------------------------------------------

const ET_DAY: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** The America/New_York calendar date + weekday for an instant. Intl is the
 *  one correct way to do this in a browser — it tracks EST/EDT for us. */
const etParts = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The current week's identity: the ET date of its MONDAY, as 'YYYY-MM-DD'.
 *  Flips exactly at Monday 00:00 ET (the ET calendar date is what rolls). */
export function etWeekKey(now: Date = new Date()): string {
  const parts: Record<string, string> = {};
  for (const p of etParts.formatToParts(now)) parts[p.type] = p.value;
  const dayIdx = ET_DAY[parts.weekday] ?? 0;
  // Walk back to Monday on a pure calendar-date number line (UTC noon guards
  // against any DST-adjacent off-by-one when subtracting days).
  const utc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12);
  const monday = new Date(utc - dayIdx * 86_400_000);
  const mm = String(monday.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(monday.getUTCDate()).padStart(2, '0');
  return `${monday.getUTCFullYear()}-${mm}-${dd}`;
}

/** Skill Points earned this week (0 if the counter is from a previous week). */
export function weeklyPoints(save: SaveData, now: Date = new Date()): number {
  return save.weekly.key === etWeekKey(now) ? save.weekly.points : 0;
}

// ---------------------------------------------------------------------------
// THE SHOP (M79) — spending. CAREER points (lifetimePoints) only ever grow:
// they drive vehicle unlocks, tiers and the leaderboard. Purchases draw down a
// separate spend counter, so buying a paint job can never re-lock a car or
// drop your rank. Balance = career - spent. Supabase contract: purchases
// become `purchase` ledger rows (idempotent by item id — owning is a set);
// spent = server-derived SUM of purchase prices.
// ---------------------------------------------------------------------------

/** Skill Points available TO SPEND in the shop. */
export function spBalance(save: SaveData): number {
  // M130: bought SP (purchasedPoints) is spendable exactly like earned SP,
  // and ONLY spendable — unlocks, tiers and rankings read lifetimePoints and
  // never see it (the owner's one rule).
  return Math.max(0, save.lifetimePoints + save.purchasedPoints - save.spentPoints);
}

export type PurchaseResult =
  | { ok: true; save: SaveData }
  | { ok: false; reason: 'unknown' | 'owned' | 'funds' };

/** Buy a catalog item with Skill Points. PURE — validates id, double-buy and
 *  funds; the caller equips separately (equipCosmetic). */
export function purchaseCosmetic(save: SaveData, itemId: string): PurchaseResult {
  const item = cosmeticById(itemId);
  if (!item) return { ok: false, reason: 'unknown' };
  if (save.cosmetics.owned.includes(itemId)) return { ok: false, reason: 'owned' };
  if (spBalance(save) < item.price) return { ok: false, reason: 'funds' };
  const next = structuredClone(save);
  next.spentPoints += item.price;
  next.cosmetics.owned.push(itemId);
  return { ok: true, save: next };
}

/** Equip an OWNED item on a vehicle (itemId null = back to stock). PURE.
 *  Ignores invalid requests (unowned/unknown/category mismatch) — returns the
 *  save unchanged, so a stale UI click can never corrupt the loadout.
 *
 *  M87: kit paints + flag wraps are ONE merged "Paint Jobs" slot — equipping
 *  either UNEQUIPS the other. (Pre-M87 both could sit equipped at once and the
 *  wrap silently won on the body while the shop still badged the paint
 *  EQUIPPED — the owner's "my paint job doesn't show in races" bug.) */
export function equipCosmetic(
  save: SaveData,
  vehicleId: string,
  category: CosmeticCategory,
  itemId: string | null,
): SaveData {
  // M128: mods stack, so they have their own paths — null clears them all.
  if (category === 'mod')
    return itemId === null ? clearMods(save, vehicleId) : equipMod(save, vehicleId, itemId, true);
  if (itemId !== null) {
    const item = cosmeticById(itemId);
    if (!item || item.category !== category || !save.cosmetics.owned.includes(itemId)) {
      return save;
    }
  }
  const next = structuredClone(save);
  const loadout: VehicleLoadout = next.cosmetics.loadouts[vehicleId] ?? {};
  if (itemId === null) delete loadout[category];
  else {
    loadout[category] = itemId;
    if (category === 'paint') delete loadout.wrap;
    else if (category === 'wrap') delete loadout.paint;
  }
  if (Object.keys(loadout).length === 0) delete next.cosmetics.loadouts[vehicleId];
  else next.cosmetics.loadouts[vehicleId] = loadout;
  return next;
}

/** M87 — the merged PAINT JOBS slot: whichever body treatment (kit paint OR
 *  flag wrap) the vehicle currently wears, or null for stock. */
export function equippedPaintJob(save: SaveData, vehicleId: string): string | null {
  const loadout = loadoutFor(save, vehicleId);
  return loadout.wrap ?? loadout.paint ?? null;
}

/** M87 — clear the merged PAINT JOBS slot (both body categories) on a ride. */
export function unequipPaintJob(save: SaveData, vehicleId: string): SaveData {
  return equipCosmetic(equipCosmetic(save, vehicleId, 'paint', null), vehicleId, 'wrap', null);
}

/** A vehicle's equipped loadout (empty = full stock). */
export function loadoutFor(save: SaveData, vehicleId: string): VehicleLoadout {
  return save.cosmetics.loadouts[vehicleId] ?? {};
}

// ---------------------------------------------------------------------------
// M128 — MODS. Any number of mods stack on a ride (owner: overlap is the
// player's call), so equipping is a per-item toggle, not a slot. The kit
// number is the one parameterised item: its picked number lives beside the
// ids. All pure; invalid requests return the save unchanged.
// ---------------------------------------------------------------------------

function writeLoadout(save: SaveData, vehicleId: string, loadout: VehicleLoadout): SaveData {
  const next = structuredClone(save);
  if (Object.keys(loadout).length === 0) delete next.cosmetics.loadouts[vehicleId];
  else next.cosmetics.loadouts[vehicleId] = loadout;
  return next;
}

/** The mod ids a ride wears (never the array in the save — a copy). */
export function equippedMods(save: SaveData, vehicleId: string): string[] {
  return [...(loadoutFor(save, vehicleId).mods ?? [])];
}

/** Put a mod on (`on` true, must be owned) or take it off a ride. */
export function equipMod(save: SaveData, vehicleId: string, itemId: string, on: boolean): SaveData {
  const item = cosmeticById(itemId);
  if (!item || item.category !== 'mod') return save;
  if (on && !save.cosmetics.owned.includes(itemId)) return save;
  const loadout = { ...loadoutFor(save, vehicleId) };
  const mods = (loadout.mods ?? []).filter((id) => id !== itemId);
  if (on) mods.push(itemId);
  if (mods.length > 0) loadout.mods = mods;
  else delete loadout.mods;
  return writeLoadout(save, vehicleId, loadout);
}

/** Take every mod off a ride (the STOCK card of the MODS tab). */
export function clearMods(save: SaveData, vehicleId: string): SaveData {
  const loadout = { ...loadoutFor(save, vehicleId) };
  if (!loadout.mods) return save;
  delete loadout.mods;
  return writeLoadout(save, vehicleId, loadout);
}

/** The picked kit number for a ride (1-11; the default until one is picked). */
export function kitNumberFor(save: SaveData, vehicleId: string): number {
  const n = loadoutFor(save, vehicleId).kitNumber;
  return isKitNumber(n) ? n : KIT_NUMBER_DEFAULT;
}

/** Pick a ride's kit number (1-11). Stored whether or not the Kit Number mod
 *  is equipped yet, so a pick made while browsing survives the buy. */
export function setKitNumber(save: SaveData, vehicleId: string, n: number): SaveData {
  if (!isKitNumber(n)) return save;
  const loadout = { ...loadoutFor(save, vehicleId), kitNumber: n };
  return writeLoadout(save, vehicleId, loadout);
}

/** M128c — the picked Fan Tifo design for a ride (1-3; the smiley until picked). */
export function tifoStyleFor(save: SaveData, vehicleId: string): number {
  const n = loadoutFor(save, vehicleId).tifoStyle;
  return isTifoStyle(n) ? n : TIFO_STYLE_DEFAULT;
}

/** Pick a ride's Fan Tifo design (1-3). Stored whether or not the cape is
 *  equipped yet, so a pick made while browsing survives the buy. */
export function setTifoStyle(save: SaveData, vehicleId: string, n: number): SaveData {
  if (!isTifoStyle(n)) return save;
  const loadout = { ...loadoutFor(save, vehicleId), tifoStyle: n };
  return writeLoadout(save, vehicleId, loadout);
}

// ---------------------------------------------------------------------------
// Skill tiers — the career rank a Skill-Point total reads as (racing-game
// convention: a named badge on the leaderboard + title strip). Presentation
// only; nothing in the sim or the unlock ladder reads these.
// ---------------------------------------------------------------------------

export interface SkillTier {
  name: string;
  /** Minimum Skill Points for the tier. MUST stay ascending. */
  min: number;
  /** Badge accent (matches the game's chip accent palette). */
  color: string;
}

export const SKILL_TIERS: readonly SkillTier[] = [
  { name: 'ROOKIE', min: 0, color: '#9fb2c4' },
  { name: 'CONTENDER', min: 100, color: '#6ef0b9' },
  { name: 'PRO', min: 300, color: '#38e1ff' },
  { name: 'ELITE', min: 700, color: '#b07cff' },
  { name: 'MASTER', min: 1500, color: '#ff7a3c' },
  { name: 'LEGEND', min: 3000, color: '#ffd34a' },
];

/** The tier a Skill-Point total sits in. */
export function skillTier(points: number): SkillTier {
  let tier = SKILL_TIERS[0];
  for (const t of SKILL_TIERS) if (points >= t.min) tier = t;
  return tier;
}

/** The next tier up (null once the player is a LEGEND). */
export function nextSkillTier(points: number): SkillTier | null {
  return SKILL_TIERS.find((t) => t.min > points) ?? null;
}
